/**
 * Notifications — campaign composer + history (FREE / Spark plan).
 *  - Compose: title, body, image (URL or Storage upload), deep link, audience.
 *  - Save: create a push_campaigns doc, then ask the sync worker to deliver it
 *    right away (it also sweeps every minute as a backstop).
 *  - Schedule: create the doc with scheduledAt; the worker sends it at that time.
 *  - History: realtime list with Sent / Sending… / Scheduled state and the
 *    delivered device count (both stamped by the worker).
 */
import { useMemo, useState, type ReactNode } from 'react';
import type { PushAudience } from '@mitang/shared';
import { useAuth } from '../lib/auth';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/errors';
import { usePushCampaigns, useUsers } from '../hooks/useFirestore';
import { createCampaign, uploadCampaignImage, tsToMs } from '../hooks/useCampaigns';
import { Card, SectionHeader, Button, Badge } from '../components/ui';
import { EmptyState, ErrorState, LoadingState } from '../components/States';
import { formatDateTime } from '../lib/format';

const AUDIENCE_OPTIONS: { value: PushAudience; label: string; hint: string }[] = [
  { value: 'all', label: 'All dealers', hint: 'Every approved user' },
  { value: 'active7d', label: 'Active last 7 days', hint: 'Recently engaged users' },
  { value: 'city', label: 'By city', hint: 'One city' },
  { value: 'users', label: 'Selected users', hint: 'Pick specific dealers' },
];

export function NotificationsPage(): JSX.Element {
  const { user } = useAuth();
  const toast = useToast();
  const campaigns = usePushCampaigns();
  const users = useUsers();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [deepLink, setDeepLink] = useState('');
  const [audience, setAudience] = useState<PushAudience>('all');
  const [city, setCity] = useState('');
  const [selectedUids, setSelectedUids] = useState<string[]>([]);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const cities = useMemo(
    () => Array.from(new Set(users.data.map((u) => u.city).filter(Boolean))).sort(),
    [users.data],
  );

  const sortedHistory = useMemo(() => {
    return [...campaigns.data].sort((a, b) => {
      const at = tsToMs((a as unknown as { createdAt?: unknown }).createdAt) ?? a.sentAt ?? a.scheduledAt ?? 0;
      const bt = tsToMs((b as unknown as { createdAt?: unknown }).createdAt) ?? b.sentAt ?? b.scheduledAt ?? 0;
      return bt - at;
    });
  }, [campaigns.data]);

  const resetForm = () => {
    setTitle('');
    setBody('');
    setImageUrl('');
    setDeepLink('');
    setAudience('all');
    setCity('');
    setSelectedUids([]);
    setScheduleEnabled(false);
    setScheduleAt('');
  };

  const onUpload = async (file: File) => {
    setUploading(true);
    try {
      const url = await uploadCampaignImage(file);
      setImageUrl(url);
      toast.success('Image uploaded.');
    } catch (e) {
      toast.error(`Upload failed: ${errorMessage(e)}`);
    } finally {
      setUploading(false);
    }
  };

  const buildAudienceValue = (): string[] | string | undefined => {
    if (audience === 'city') return city.trim() || undefined;
    if (audience === 'users') return selectedUids;
    return undefined;
  };

  const validate = (): string | null => {
    if (!title.trim()) return 'Title is required.';
    if (!body.trim()) return 'Body is required.';
    if (audience === 'city' && !city.trim()) return 'Pick a city for the city audience.';
    if (audience === 'users' && selectedUids.length === 0)
      return 'Select at least one user for the selected-users audience.';
    if (scheduleEnabled) {
      const t = new Date(scheduleAt).getTime();
      if (Number.isNaN(t)) return 'Pick a valid schedule date/time.';
      if (t <= Date.now()) return 'Schedule time must be in the future.';
    }
    return null;
  };

  const onSubmit = async () => {
    if (!user) return;
    const problem = validate();
    if (problem) {
      toast.error(problem);
      return;
    }
    setSubmitting(true);
    try {
      const scheduledAt = scheduleEnabled ? new Date(scheduleAt).getTime() : undefined;
      await createCampaign({
        title: title.trim(),
        body: body.trim(),
        imageUrl: imageUrl.trim() || undefined,
        deepLink: deepLink.trim() || undefined,
        audience,
        audienceValue: buildAudienceValue(),
        scheduledAt,
        createdBy: user.uid,
      });

      if (scheduledAt) {
        toast.success('Campaign scheduled — it will be delivered at the set time.');
      } else {
        toast.success('Campaign sent. Delivery counts appear in the history in a few seconds.');
      }
      resetForm();
    } catch (e) {
      toast.error(`Could not create campaign: ${errorMessage(e)}`);
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Notifications"
        subtitle="Compose push campaigns for dealers."
      />

      <div className="flex items-start gap-3 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
        <span aria-hidden className="mt-0.5 text-base">ℹ</span>
        <p>
          Campaigns are <strong>delivered immediately</strong> on save (scheduled ones at their set
          time). A dealer only receives it if they have opened the app and allowed notifications —
          the count below is <strong>devices</strong>, not people — one dealer with the app and the
          website open counts twice.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        {/* Composer */}
        <Card className="xl:col-span-3">
          <div className="border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-semibold text-slate-900">New campaign</h2>
          </div>
          <div className="space-y-4 p-5">
            <FormField label="Title" required>
              <input
                type="text"
                value={title}
                maxLength={80}
                onChange={(e) => setTitle(e.target.value)}
                className={inputCls}
                placeholder="e.g. New HP Victus arrivals"
              />
            </FormField>

            <FormField label="Body" required>
              <textarea
                value={body}
                rows={3}
                maxLength={240}
                onChange={(e) => setBody(e.target.value)}
                className={inputCls}
                placeholder="Short message shown in the notification."
              />
            </FormField>

            <FormField label="Image (optional)" hint="URL or upload (≤5MB, image/*)">
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  className={inputCls}
                  placeholder="https://…"
                />
                <label className="inline-flex cursor-pointer items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  {uploading ? 'Uploading…' : 'Upload'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void onUpload(f);
                    }}
                  />
                </label>
              </div>
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt="Campaign preview"
                  className="mt-2 h-24 rounded-md border border-slate-200 object-cover"
                />
              )}
            </FormField>

            <FormField label="Deep link (optional)" hint="product/{id} or category/{name}">
              <input
                type="text"
                value={deepLink}
                onChange={(e) => setDeepLink(e.target.value)}
                className={inputCls}
                placeholder="product/LAPTOPS_ABC123"
              />
            </FormField>

            <FormField label="Audience" required>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {AUDIENCE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setAudience(opt.value)}
                    className={[
                      'rounded-lg border px-3 py-2 text-left text-xs transition-colors',
                      audience === opt.value
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : 'border-slate-300 text-slate-600 hover:bg-slate-50',
                    ].join(' ')}
                  >
                    <span className="block font-semibold">{opt.label}</span>
                    <span className="block text-[10px] text-slate-400">{opt.hint}</span>
                  </button>
                ))}
              </div>
            </FormField>

            {audience === 'city' && (
              <FormField label="City">
                <select value={city} onChange={(e) => setCity(e.target.value)} className={inputCls}>
                  <option value="">Select a city…</option>
                  {cities.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </FormField>
            )}

            {audience === 'users' && (
              <FormField label={`Selected users (${selectedUids.length})`}>
                <div className="max-h-48 overflow-y-auto rounded-md border border-slate-200">
                  {users.loading ? (
                    <div className="p-3">
                      <LoadingState label="Loading users…" />
                    </div>
                  ) : (
                    users.data
                      .filter((u) => u.status === 'approved')
                      .map((u) => {
                        const checked = selectedUids.includes(u.id);
                        return (
                          <label
                            key={u.id}
                            className="flex cursor-pointer items-center gap-2 border-b border-slate-100 px-3 py-2 text-sm last:border-0 hover:bg-slate-50"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) =>
                                setSelectedUids((prev) =>
                                  e.target.checked
                                    ? [...prev, u.id]
                                    : prev.filter((id) => id !== u.id),
                                )
                              }
                            />
                            <span className="flex-1 truncate">
                              {u.name} · <span className="text-slate-400">{u.companyName}</span>
                            </span>
                            <span className="font-mono text-[11px] text-slate-400">{u.mobile}</span>
                          </label>
                        );
                      })
                  )}
                </div>
              </FormField>
            )}

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={scheduleEnabled}
                  onChange={(e) => setScheduleEnabled(e.target.checked)}
                />
                Schedule for later
              </label>
              {scheduleEnabled && (
                <div className="mt-2">
                  <input
                    type="datetime-local"
                    value={scheduleAt}
                    onChange={(e) => setScheduleAt(e.target.value)}
                    className={inputCls}
                  />
                  <p className="mt-1 text-[11px] text-amber-600">
                    Note: scheduling only stores <code>scheduledAt</code>. The external push script
                    dispatches due campaigns — this panel does not send at the scheduled time.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={resetForm} disabled={busy}>
                Reset
              </Button>
              <Button onClick={() => void onSubmit()} loading={busy}>
                {scheduleEnabled ? 'Schedule campaign' : 'Save campaign'}
              </Button>
            </div>
          </div>
        </Card>

        {/* History */}
        <Card className="xl:col-span-2">
          <div className="border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-semibold text-slate-900">History</h2>
            <p className="text-xs text-slate-500">Recent campaigns and delivery</p>
          </div>
          <div className="max-h-[36rem] overflow-y-auto">
            {campaigns.loading ? (
              <div className="p-4">
                <LoadingState label="Loading history…" />
              </div>
            ) : campaigns.error ? (
              <div className="p-4">
                <ErrorState message={campaigns.error} />
              </div>
            ) : sortedHistory.length === 0 ? (
              <div className="p-4">
                <EmptyState title="No campaigns yet" hint="Sent and scheduled campaigns appear here." />
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {sortedHistory.map((c) => {
                  const scheduled = c.scheduledAt && !c.sentAt;
                  return (
                    <li key={c.id} className="px-5 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
                          {c.title}
                        </p>
                        {scheduled ? (
                          <Badge tone="warn">Scheduled</Badge>
                        ) : c.sentAt ? (
                          <Badge tone="success">Sent</Badge>
                        ) : (
                          <Badge tone="warn">Sending…</Badge>
                        )}
                      </div>
                      <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{c.body}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] text-slate-400">
                        <span>audience: {audienceLabel(c.audience, c.audienceValue)}</span>
                        {scheduled ? (
                          <span>for {formatDateTime(c.scheduledAt)}</span>
                        ) : (
                          <span>{formatDateTime(c.sentAt)}</span>
                        )}
                        {typeof c.deliveredCount === 'number' && (
                          <span
                            className="font-medium text-emerald-600"
                            title="Devices that received it — one dealer can have several (phone app + browser)."
                          >
                            {c.deliveredCount} {c.deliveredCount === 1 ? 'device' : 'devices'}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

const inputCls =
  'w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500';

function FormField({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <div>
      <label className="mb-1 flex items-center gap-1 text-xs font-medium text-slate-600">
        {label}
        {required && <span className="text-red-500">*</span>}
        {hint && <span className="ml-auto text-[10px] font-normal text-slate-400">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

function audienceLabel(audience: PushAudience, value?: string[] | string): string {
  switch (audience) {
    case 'all':
      return 'all dealers';
    case 'active7d':
      return 'active 7d';
    case 'city':
      return `city: ${typeof value === 'string' ? value : '—'}`;
    case 'users':
      return `${Array.isArray(value) ? value.length : 0} users`;
    default:
      return audience;
  }
}
